import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { faqs } from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    grade: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/inquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast({
          title: "Inquiry Submitted!",
          description: "We'll contact you within 24 hours.",
        });
        setFormData({
          name: '',
          email: '',
          phone: '',
          grade: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit inquiry. Please try again or call us directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="heading-hero mb-4">Get in Touch</h1>
          <p className="body-large text-[var(--text-secondary)] max-w-2xl mx-auto">
            Have questions? We're here to help. Contact us or visit our institute in Noida.
          </p>
        </div>

        {/* Contact Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <a
            href="tel:+917004929531"
            className="bg-[var(--accent-purple-200)] p-6 rounded-xl text-center hover-lift"
          >
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <Phone size={24} className="text-[var(--text-primary)]" />
            </div>
            <h3 className="heading-3 mb-2">Phone</h3>
            <p className="body-small text-[var(--text-secondary)] font-mono mb-2">
              +91 70049 29531
            </p>
            <p className="body-small text-[var(--text-secondary)] font-mono">
              +91 91621 84962
            </p>
            <p className="caption mt-2">Available 24 Hours</p>
          </a>

          <a
            href="mailto:thelighthousedu@gmail.com"
            className="bg-[var(--accent-blue-200)] p-6 rounded-xl text-center hover-lift"
          >
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail size={24} className="text-[var(--text-primary)]" />
            </div>
            <h3 className="heading-3 mb-2">Email</h3>
            <p className="body-small text-[var(--text-secondary)] break-all">
              thelighthousedu@gmail.com
            </p>
            <p className="caption mt-2">Response within 24 hours</p>
          </a>

          <div className="bg-[var(--accent-orange-200)] p-6 rounded-xl text-center">
            <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
              <MapPin size={24} className="text-[var(--text-primary)]" />
            </div>
            <h3 className="heading-3 mb-2">Location</h3>
            <p className="body-small text-[var(--text-secondary)]">
              Shop No. 05, Near Defence Enclave Main Gate<br />
              Sector 44, Noida, UP
            </p>
            <p className="caption mt-2">Visit us anytime</p>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
          {/* Contact Form */}
          <div className="bg-[var(--bg-card)] p-8 rounded-2xl shadow-sm">
            <h2 className="heading-2 mb-6">Send Us an Inquiry</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  Student Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-[var(--border-input)] rounded-xl focus:outline-none focus:border-[var(--border-input-focus)] transition-colors bg-white"
                  placeholder="Enter student name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[var(--border-input)] rounded-xl focus:outline-none focus:border-[var(--border-input-focus)] transition-colors bg-white"
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[var(--border-input)] rounded-xl focus:outline-none focus:border-[var(--border-input-focus)] transition-colors bg-white"
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Class/Grade *
                  </label>
                  <select
                    name="grade"
                    value={formData.grade}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-[var(--border-input)] rounded-xl focus:outline-none focus:border-[var(--border-input-focus)] transition-colors bg-white"
                  >
                    <option value="">Select Grade</option>
                    {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={`Class ${i + 1}`}>
                        Class {i + 1}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Subject Interest
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-[var(--border-input)] rounded-xl focus:outline-none focus:border-[var(--border-input-focus)] transition-colors bg-white"
                  >
                    <option value="">Select Subject</option>
                    <option value="Mathematics">Mathematics</option>
                    <option value="Physics">Physics</option>
                    <option value="Chemistry">Chemistry</option>
                    <option value="Biology">Biology</option>
                    <option value="English">English</option>
                    <option value="Commerce">Commerce Subjects</option>
                    <option value="All">All Subjects</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  Message
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-[var(--border-input)] rounded-xl focus:outline-none focus:border-[var(--border-input-focus)] transition-colors bg-white resize-none"
                  placeholder="Tell us about your requirements..."
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary flex items-center justify-center gap-2"
              >
                {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                <Send size={18} />
              </button>
            </form>
          </div>

          {/* Map */}
          <div className="space-y-6">
            {/* Map */}
            <div className="bg-[var(--bg-card)] p-8 rounded-2xl shadow-sm">
              <h2 className="heading-2 mb-4">Visit Our Institute</h2>
              <div className="aspect-video bg-[var(--bg-section)] rounded-xl flex items-center justify-center">
                <div className="text-center">
                  <MapPin size={48} className="mx-auto mb-3 text-[var(--text-muted)]" />
                  <p className="body-small text-[var(--text-secondary)]">
                    Shop No. 05, Near Defence Enclave Main Gate<br />
                    Sector 44, Noida, Uttar Pradesh
                  </p>
                  <a
                    href="https://share.google/rJ3ZchhUnPZ2jlxv2"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-4 text-sm font-medium text-[var(--text-primary)] hover:underline"
                  >
                    Open in Google Maps →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQs */}
        <div className="bg-[var(--bg-card)] p-8 md:p-12 rounded-2xl shadow-sm">
          <h2 className="heading-1 mb-8 text-center">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={`item-${faq.id}`}
                className="border border-[var(--border-light)] rounded-xl px-6 bg-white"
              >
                <AccordionTrigger className="text-left heading-3 py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="body-small text-[var(--text-secondary)] pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </div>
  );
};

export default Contact;
